import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  TextField,
  Button,
  Alert,
  Divider
} from '@mui/material';
import PDFAnalysisComponent from './PDFAnalysisComponent';
import PDFPreview from './PDFPreview';

const PDFAnalysisDemo = () => {
  const [testUrl, setTestUrl] = useState('');
  const [showDemo, setShowDemo] = useState(false);

  // ตัวอย่าง Google Drive URLs สำหรับทดสอบ
  const sampleUrls = [
    {
      name: 'รายงานฝึกงานตัวอย่าง 1',
      url: 'https://drive.google.com/file/d/1234567890abcdef/view?usp=sharing',
      description: 'รายงานการฝึกงานด้าน Software Development'
    },
    {
      name: 'รายงานฝึกงานตัวอย่าง 2', 
      url: 'https://drive.google.com/file/d/abcdef1234567890/view?usp=sharing',
      description: 'รายงานการฝึกงานด้าน Data Science'
    }
  ];

  const handleAnalyzeDemo = () => {
    if (testUrl.trim()) {
      setShowDemo(true);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          🤖 Typhoon OCR + AI Analysis Demo
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          ระบบวิเคราะห์เอกสารรายงานฝึกงาน/สหกิจศึกษาด้วย AI
        </Typography>
      </Box>

      <Card elevation={3} sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            📋 วิธีใช้งาน
          </Typography>
          <Typography variant="body1" paragraph>
            1. ใส่ลิงค์ Google Drive ของไฟล์ PDF รายงานฝึกงาน/สหกิจศึกษา
          </Typography>
          <Typography variant="body1" paragraph>
            2. คลิกปุ่ม "เริ่มทดสอบการวิเคราะห์"
          </Typography>
          <Typography variant="body1" paragraph>
            3. ระบบจะทำการ OCR และวิเคราะห์เนื้อหาอัตโนมัติ
          </Typography>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>หมายเหตุ:</strong> ไฟล์ต้องเป็น PDF และมีการตั้งค่าให้สามารถเข้าถึงได้ (Anyone with the link can view)
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      <Card elevation={3} sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            🧪 ทดสอบการวิเคราะห์
          </Typography>
          
          <TextField
            fullWidth
            label="ลิงค์ Google Drive PDF"
            value={testUrl}
            onChange={(e) => setTestUrl(e.target.value)}
            placeholder="https://drive.google.com/file/d/FILE_ID/view?usp=sharing"
            variant="outlined"
            sx={{ mb: 2 }}
          />
          
          <Button
            variant="contained"
            size="large"
            onClick={handleAnalyzeDemo}
            disabled={!testUrl.trim()}
            sx={{ mb: 3 }}
          >
            เริ่มทดสอบการวิเคราะห์
          </Button>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            📁 ตัวอย่างลิงค์สำหรับทดสอบ
          </Typography>
          {sampleUrls.map((sample, index) => (
            <Card key={index} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold">
                  {sample.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {sample.description}
                </Typography>
                <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                  {sample.url}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setTestUrl(sample.url);
                    }}
                  >
                    ใช้ลิงค์นี้
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </CardContent>
      </Card>

      {showDemo && testUrl && (
        <>
          <Card elevation={3} sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                📄 ตัวอย่าง PDF
              </Typography>
              <PDFPreview
                googleDriveUrl={testUrl}
                height="400px"
                title="เอกสารทดสอบ"
                showFullscreenButton={true}
              />
            </CardContent>
          </Card>

          <PDFAnalysisComponent
            googleDriveUrl={testUrl}
            requestId="DEMO-001"
            title="เอกสารทดสอบการวิเคราะห์"
          />
        </>
      )}

      <Card elevation={3} sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            🎯 คุณสมบัติของระบบ
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
            <Box>
              <Typography variant="h6" color="primary" gutterBottom>
                📖 OCR ภาษาไทย
              </Typography>
              <Typography variant="body2">
                ใช้ Typhoon OCR API ที่รองรับภาษาไทยได้ดีเยี่ยม สามารถสกัดข้อความจาก PDF ได้อย่างแม่นยำ
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="h6" color="primary" gutterBottom>
                🧠 AI Analysis
              </Typography>
              <Typography variant="body2">
                วิเคราะห์เนื้อหาด้วย AI จำแนกทักษะ กิจกรรม ความรู้ และให้ข้อเสนอแนะที่เป็นประโยชน์
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="h6" color="primary" gutterBottom>
                📊 รายงานโดยละเอียด
              </Typography>
              <Typography variant="body2">
                แสดงผลการวิเคราะห์ในรูปแบบที่เข้าใจง่าย พร้อมการจัดหมวดหมู่ที่ชัดเจน
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="h6" color="primary" gutterBottom>
                🎨 UI/UX ที่ใช้งานง่าย
              </Typography>
              <Typography variant="body2">
                ออกแบบให้ใช้งานง่าย ตอบสนองได้ดีบนทุกอุปกรณ์ และมีการแสดงผลที่สวยงาม
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Powered by Typhoon AI • Built with React & Material-UI
        </Typography>
      </Box>
    </Container>
  );
};

export default PDFAnalysisDemo;
